import React from "react";
import { Divider, Grid, Icon } from "semantic-ui-react";
import { ValidatedForm } from "../../../../../../../../components/forms";
import {
  type RecurrenceSession,
  type SessionPayload,
  type SessionsView,
  type SessionView,
} from "../../../../../types/state";
import { getPropName } from "../../../Utils/getSessionPropName";
import { RecurringSessionDescription } from "./RecurringSessionDescription";

import { faUserGroup } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { keyboardInterceptors } from "../../../../../../../../utils/components/inputUtils";
import { type ArrayHelpers, type FormikProps } from "formik";
import { ErrorPositions } from "../../../../../../../../enums/errorPositions";
import RecurringSessionFormBuilderRow from "../RecurringSessionFormBuilderRow/RecurringSessionFormBuilderRow";
import { getRecurringSession } from "../../../Utils/utils";
import moment from "moment";

import styles from "./RecurringSessionFormBuilder.module.scss";

const maxRegistrationLimitLength: number = 5;

export type RecurringSessionFormBuilderProps = CommonRecurringSessionBuilderProps & {
  sessions: SessionView[];
};

export interface CommonRecurringSessionBuilderProps {
  validatedFieldProps: FormikProps<SessionsView>;
  arrayHelpers: ArrayHelpers;
  disabled?: boolean;
  onBlur: (propertyName: string) => void;
  onChanged: () => void;
}

export const RecurringSessionFormBuilder = (props: RecurringSessionFormBuilderProps) => {
  const { sessions, disabled, onBlur, validatedFieldProps, arrayHelpers, onChanged } = props;

  const getRecurrentSessionPropName = (name: keyof RecurrenceSession): string =>
    `${getPropName<SessionPayload>("recurrenceSession")}.${name}`;

  const onAddSession = () => {
    arrayHelpers.push(getRecurringSession(sessions[0]));
    setTimeout(() => onChanged, 0);
  };

  const onRemoveSession = (index: number) => {
    arrayHelpers.remove(index);
    setTimeout(() => onChanged, 0);
  };

  const onHandleDateChange = async (
    _event: React.SyntheticEvent<HTMLElement>,
    data: { name: string; value: string },
  ) => {
    const updates = sessions.map((_, idx) =>
      validatedFieldProps.setFieldValue(getPropName<SessionView>("startDate", idx), data.value),
    );
    await Promise.all(updates);
    onBlur(getPropName<SessionPayload>("sessionStartDate"));
    setTimeout(() => onChanged, 0);
  };

  const onHandleEndDateChange = async (
    _event: React.SyntheticEvent<HTMLElement>,
    data: { name: string; value: string },
  ) => {
    const updates = sessions.map((_, idx) =>
      validatedFieldProps.setFieldValue(
        `${getPropName<SessionView>("recurrenceSession", idx)}.seriesEndDate`,
        data.value,
      ),
    );
    await Promise.all(updates);
    onBlur?.(`${getPropName<SessionView>("recurrenceSession")}.seriesEndDate`);
    setTimeout(() => onChanged, 0);
  };

  return (
    <Grid stackable columns="equal" verticalAlign="bottom">
      <Grid.Row>
        <Grid.Column className={styles.containerColumn}>
          <div className={styles.containerFields}>
            <div className={styles.sessionUrl}>
              <ValidatedForm.InputField
                label="Event URL"
                value={sessions[0].webinarUrl}
                propertyName={getPropName<SessionView>("webinarUrl")}
                markAsRequired
                onBlur={onBlur}
                disabled={disabled}
                errorPosition="bottom"
                {...validatedFieldProps}
              />
            </div>
            <div className={styles.sessionRegistrationLimit}>
              <ValidatedForm.InputField
                placeholder="No limit"
                label="Registration Limit"
                value={sessions[0].registrationLimit}
                propertyName={getPropName<SessionView>("registrationLimit")}
                disabled={disabled}
                errorPosition="bottom"
                onBlur={onBlur}
                maxLength={maxRegistrationLimitLength}
                onKeyPress={keyboardInterceptors.integerOnly}
                {...validatedFieldProps}
                icon={<FontAwesomeIcon icon={faUserGroup} />}
                iconPosition="left"
              />
            </div>
            <div>
              <ValidatedForm.DatePickerField
                label="Start date"
                selected={sessions[0].startDate || ""}
                propertyName={getPropName<SessionView>("startDate")}
                disabled={disabled}
                markAsRequired
                errorPosition={ErrorPositions.bottom}
                onDateChange={onHandleDateChange}
                {...validatedFieldProps}
              />
            </div>
            <div>
              <ValidatedForm.DatePickerField
                label="End date"
                selected={sessions[0].recurrenceSession?.seriesEndDate || ""}
                propertyName={getRecurrentSessionPropName("seriesEndDate")}
                disabled={disabled}
                maxDate={moment(sessions[0].startDate, "MM/DD/YYYY").add(36, "months").format("MM/DD/YYYY")}
                markAsRequired
                errorPosition={ErrorPositions.bottom}
                onDateChange={onHandleEndDateChange}
                {...validatedFieldProps}
              />
            </div>
          </div>
        </Grid.Column>
      </Grid.Row>
      {sessions.map((session: SessionView, index: number) => (
        <React.Fragment key={index}>
          <RecurringSessionFormBuilderRow
            session={session}
            arrayHelpers={arrayHelpers}
            validatedFieldProps={validatedFieldProps}
            disabled={disabled}
            index={index}
            hideRemove={sessions.length < 2}
            onRemove={onRemoveSession}
            onBlur={onBlur}
            onChanged={onChanged}
          />
          <Divider className={styles.divider} />
        </React.Fragment>
      ))}
      {!disabled && (
        <Grid.Row>
          <Grid.Column>
            <div className={styles.addSession} onClick={onAddSession}>
              <Icon className="plus circle icon" size="big" color="blue" />
              <span>Add recurring session</span>
            </div>
          </Grid.Column>
        </Grid.Row>
      )}

      <Grid.Row>
        <Grid.Column>
          <div className={styles.descriptionWrapper}>
            <Icon link className="fa fa-sync" size="big" />
            <div>
              {sessions.map((session, index) => (
                <span key={index}>
                  <RecurringSessionDescription session={session} />
                </span>
              ))}
            </div>
          </div>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};
